import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  take,
} from 'rxjs';
import {
  UserAreaBulkUploadResult,
  UserAreaModel,
} from '../../../../models/user-area.model';
import { AlertService } from '../../../../services/alert.service';
import { UserAreaService } from '../../../../services/user-area.service';
import {
  BulkUploadMemberAreasDialogComponent,
  BulkUploadMemberAreasDialogResult,
} from '../../../shared-components/bulk-upload-member-areas-dialog/bulk-upload-member-areas-dialog.component';
import {
  TableColumn,
  TableHeaderButton,
} from '../../../shared-components/custom-table/custom-table.component';

@Component({
  selector: 'app-member-areas',
  templateUrl: './member-areas.component.html',
  styleUrl: './member-areas.component.scss',
  standalone: false,
})
export class MemberAreasAdministrationComponent {
  public dataSource$: Observable<UserAreaModel[]>;
  public loading = false;
  public bulkUploading = false;
  public memberAreas: UserAreaModel[] = [];

  public tableHeaderButton: TableHeaderButton = {
    label: 'Mitgliederbereich erstellen',
    icon: 'fa-plus',
    onClick: () => {
      this.router.navigate(['create'], { relativeTo: this.activatedRoute });
    },
  };

  constructor(
    private userAreaService: UserAreaService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.getMemberAreas();
  }

  private getMemberAreas() {
    this.loading = true;
    this.dataSource$ = this.userAreaService.getAllAreas().pipe(
      take(1),
      map(areas => areas.map(area => area)),
      map(areas => {
        this.memberAreas = areas;
        return areas;
      }),
      catchError(error => {
        this.loading = false;
        console.error('Error fetching member areas', error);
        this.memberAreas = [];
        return of([]);
      }),
      finalize(() => {
        this.loading = false;
      }),
      shareReplay(1)
    );
  }

  public columns: TableColumn[] = [
    {
      columnDef: 'name',
      header: 'Name',
      width: '60%',
      sortable: true,
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'owner',
      header: 'Eigentümer',
      width: '30%',
      sortable: true,
      render: (row: UserAreaModel) =>
        row.owner ? row.owner.firstName + ' ' + row.owner.lastName : '-',
    },
    {
      columnDef: 'isPublic',
      header: 'Öffentlich',
      width: '10%',
      sortable: true,
      render: (row: UserAreaModel) => (row.isPublic ? 'Ja' : 'Nein'),
    },
    {
      columnDef: 'memberCount',
      header: 'Mitglieder',
      width: '10%',
      sortable: true,
      render: (row: UserAreaModel) => {
        if (row.isPublic) return 'Alle';
        return row.memberCount ? String(row.memberCount) : '-';
      },
    },
    {
      columnDef: 'actions',
      header: 'Aktionen',
      headerAlign: 'center',
      cellAlign: 'center',
      width: '10%',
      fontWeight: '400',
      render: () =>
        `<button class="table-action-button"><i class="fa-regular fa-pen"></i></button>`,
      onCellClick: this.onEditMemberArea.bind(this),
    },
  ];

  // Define the function that will be passed to the table component
  public onCellClick(row: UserAreaModel, column: any) {
    this.router.navigate([btoa(String(row.id))], {
      relativeTo: this.activatedRoute,
    });
  }

  public onEditMemberArea(row: UserAreaModel) {
    this.router.navigate(['edit', btoa(String(row.id))], {
      relativeTo: this.activatedRoute,
    });
  }

  public openBulkUploadModal(): void {
    const dialogRef = this.dialogService.open(
      BulkUploadMemberAreasDialogComponent,
      {
        width: '720px',
        contentStyle: { overflow: 'auto' },
        data: {
          memberAreas: this.memberAreas,
        },
      }
    );

    dialogRef.onClose
      .pipe(take(1))
      .subscribe((result: BulkUploadMemberAreasDialogResult | undefined) => {
        if (!result || !result.file || result.areaIds.length === 0) return;
        this.submitBulkUpload(result);
      });
  }

  private submitBulkUpload(result: BulkUploadMemberAreasDialogResult): void {
    if (this.bulkUploading) return;
    this.bulkUploading = true;
    this.userAreaService
      .uploadAreaFileBulk(result.areaIds, result.file)
      .pipe(
        take(1),
        finalize(() => {
          this.bulkUploading = false;
        })
      )
      .subscribe({
        next: result => {
          this.handleBulkUploadResult(result);
          this.getMemberAreas();
        },
        error: err => {
          this.alertService.showErrorAlert('Upload fehlgeschlagen.');
        },
      });
  }

  private handleBulkUploadResult(result: UserAreaBulkUploadResult): void {
    if (result.failedAreas.length === 0) {
      this.alertService.showSuccessAlert(
        `Datei wurde in ${result.totalUploaded} Mitgliederbereiche hochgeladen.`
      );
      return;
    }

    if (result.totalUploaded > 0) {
      this.alertService.showInfoAlert(
        `Upload teilweise erfolgreich: ${result.totalUploaded} von ${result.totalRequested} Bereiche veröffentlicht.`
      );
      return;
    }

    this.alertService.showErrorAlert(
      'Upload fehlgeschlagen. Für keinen ausgewählten Bereich besteht eine Berechtigung.'
    );
  }
}
